/**
 * Account module (#92): the former account settings modal as a routed
 * page. #/account/profile and #/account/security select the tab.
 */

import template from './account.template.html?raw';
import type {
  default as AccountSettings,
  AccountSettingsTab,
} from '../../../components/account-settings';
import type { AppModule } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';

export const ACCOUNT_TABS: readonly AccountSettingsTab[] = [
  'profile',
  'security',
];

export interface AccountModuleDeps {
  t: (key: string, params?: TranslationParams) => string;
  getUserName: () => string | null;
  /** The AccountSettings instance ComponentInitializer built at boot. */
  getView: () => AccountSettings | null;
  navigate: (path: string) => void;
  /** #92 follow-up chrome cleanup: null in direct/dev mode (no authService). */
  logout: (() => void) | null;
  /** Re-render only while visible, mirroring the Status/Dashboard modules. */
  onLanguageChange: (callback: () => void) => void;
}

export function createAccountModule(deps: AccountModuleDeps): AppModule {
  let wired = false;
  let root: HTMLElement | null = null;
  let logoutWired = false;
  let visible = false;

  /** "Signed in as {{name}}" (#92 follow-up chrome cleanup); hidden if no name. */
  const renderSignedIn = (): void => {
    const el = root?.querySelector<HTMLElement>('[data-account-signed-in]');
    if (!el) return;
    const name = deps.getUserName();
    el.hidden = !name;
    el.textContent = name ? deps.t('modules.account.status', { name }) : '';
  };

  /** Log out button; hidden when logout is null (direct/dev mode). */
  const renderLogout = (): void => {
    const btn = root?.querySelector<HTMLButtonElement>('[data-account-logout]');
    if (!btn) return;
    btn.hidden = !deps.logout;
    if (deps.logout && !logoutWired) {
      btn.addEventListener('click', () => deps.logout?.());
      logoutWired = true;
    }
  };

  return {
    id: 'account',
    route: '/account',
    labelKey: 'modules.account.label',
    descriptionKey: 'modules.account.description',
    icon: 'account',
    enabled: true,
    showOnDashboard: true,
    subroutes: ACCOUNT_TABS,

    mount(outlet) {
      outlet.innerHTML = template;
      root = outlet;
      deps.onLanguageChange(() => {
        if (visible) renderSignedIn();
      });
    },

    onEnter(params) {
      visible = true;
      renderSignedIn();
      renderLogout();

      const view = deps.getView();
      if (!view) return;
      if (!wired) {
        view.setTabSelectHandler((tab) => deps.navigate(`/account/${tab}`));
        wired = true;
      }
      view.resume();
      view.switchTab((params.subroute ?? 'profile') as AccountSettingsTab);
    },

    onLeave() {
      visible = false;
      deps.getView()?.suspend();
    },

    getStatus() {
      const name = deps.getUserName();
      return name ? deps.t('modules.account.status', { name }) : null;
    },
  };
}
